import React, { useMemo } from 'react';
import type { CtfState, EffectId, Hex, Team, Unit } from '../../../games/capture-the-flag/types';
import { UPRIGHT_ROTATION, EFFECT_NAMES, EFFECT_TIMING, zoneHexes, zoneUp, unitLabel, hasTrait, prisonCell } from '../../../games/capture-the-flag/capture-the-flag';
import { DIRECTIONS } from '../../../games/capture-the-flag/board';

// What a click on a hex would do, for highlighting ('peek': where a looked-at unit could move).
export type HexHint = { kind: 'move' | 'reach' | 'activate' | 'tag' | 'select' | 'area' | 'drop' | 'peek'; label?: string };

const S = 20;
const SQ3 = Math.sqrt(3);
const ROT = (UPRIGHT_ROTATION * Math.PI) / 180;
const key = (h: Hex) => `${h.q},${h.r}`;

function px(h: Hex): [number, number] {
  const x = S * 1.5 * h.q, y = S * SQ3 * (h.r + h.q / 2);
  return [x * Math.cos(ROT) - y * Math.sin(ROT), x * Math.sin(ROT) + y * Math.cos(ROT)];
}
// For pictures drawn over the board (the rules page's examples): a hex's centre and the hex size.
export { px as hexPx, S as HEX_SIZE };

function hexPoints(h: Hex, scale = 1): string {
  const [cx, cy] = px(h);
  return Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 3) * k + ROT;
    return `${(cx + S * scale * Math.cos(a)).toFixed(2)},${(cy + S * scale * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
}

const TEAM = { A: '#3b82f6', B: '#ef4444' } as const;
const TINT = { A: '#1e3a8a', B: '#7f1d1d' } as const;
const HOME_TINT = { A: '#1e40af', B: '#991b1b' } as const;
const NEUTRAL = '#374151';
const GLYPH: Record<EffectId, string> = {
  swift: '+1', watchtower: 'Wt', longShot: 'LS', blink: 'Bl', magnet: 'Mg',
};

interface Props {
  state: CtfState;
  hints: Map<string, HexHint>;
  onHexClick: (h: Hex) => void;
  lastTag: { from: Hex; to: Hex; hit: boolean } | null;
  clickAll?: boolean;                // every hex is clickable (set-up mode)
  focus?: string | null;             // a unit being looked at: its guard zone is tinted
}

export default function CtfBoard({ state, hints, onHexClick, lastTag, clickAll, focus }: Props) {
  const s = state;
  const rings = s.config.rings;
  const ext = S * 1.5 * (3 * rings + 2.2) + 8;

  // Static geometry: cell of every hex, and the cell-border segments.
  const geom = useMemo(() => {
    const cellAt = new Map<string, number>();
    for (const c of s.cells) for (const h of c.hexes) cellAt.set(key(h), c.id);
    const edges: [number, number, number, number][] = [];
    for (const c of s.cells) for (const h of c.hexes) for (const d of DIRECTIONS) {
      const n = { q: h.q + d.q, r: h.r + d.r };
      const nc = cellAt.get(key(n));
      if (nc === c.id || (nc !== undefined && nc < c.id)) continue;
      const [x1, y1] = px(h), [x2, y2] = px(n);
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy), half = S / 2 + 0.6;
      edges.push([mx - (dy / len) * half, my + (dx / len) * half, mx + (dy / len) * half, my - (dx / len) * half]);
    }
    return { cellAt, edges };
  }, [s.cells]);

  const activeId = s.active?.unitId ?? null;
  const unitAtKey = new Map<string, Unit>();
  for (const u of s.units) if (u.pos) unitAtKey.set(key(u.pos), u);

  const fillFor = (id: number) => {
    const c = s.cells[id];
    if (c.home) return HOME_TINT[c.home];
    const o = s.owners[id];
    return o ? TINT[o] : NEUTRAL;
  };

  return (
    <svg viewBox={`${-ext} ${-ext} ${2 * ext} ${2 * ext}`} className="w-full h-auto select-none" role="img" aria-label="Conquer the Flag board">
      {/* Hexes */}
      {s.cells.map(c => c.hexes.map(h => (
        <polygon key={key(h)} points={hexPoints(h)} fill={fillFor(c.id)} stroke="#111827" strokeWidth={1}
          onClick={() => onHexClick(h)} style={{ cursor: clickAll || hints.has(key(h)) ? 'pointer' : 'default' }} />
      )))}
      {/* Cell borders */}
      {geom.edges.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#e5e7eb" strokeWidth={2.4} strokeLinecap="round" opacity={0.55} pointerEvents="none" />
      ))}
      {/* Cell numbers, outposts, HQ and teleporter markers */}
      {s.cells.map(c => {
        const [cx, cy] = px(c.centre);
        const corner = c.hexes.slice(1).map(h => px(h)).sort((a, b) => (a[1] + 0.6 * a[0]) - (b[1] + 0.6 * b[0]))[0];
        const fx = s.effects?.[c.id];
        return (
          <g key={`m${c.id}`} pointerEvents="none">
            <text x={corner[0]} y={corner[1] + 0.5} fontSize={10} fill="#9ca3af" textAnchor="middle" dominantBaseline="central" fontFamily="ui-monospace, monospace">{c.id}</text>
            {c.role === 'hq' && <polygon points={hexPoints(c.centre, 0.8)} fill="none" stroke={TEAM[c.home!]} strokeWidth={2.5} strokeDasharray="4 3" />}
            {c.role === 'teleporter' && <circle cx={cx} cy={cy} r={S * 0.5} fill="none" stroke={TEAM[c.home!]} strokeWidth={2.5} />}
            {s.config.prisons && (['A', 'B'] as const).filter(t => prisonCell(s, t) === c.id).map(t => {
              // A jail icon (a barred window) in the team's colour, like the HQ and teleporter marks;
              // who is inside shows on the cell card.
              const [jx, jy] = fx ? [cx, cy + S * 1.1] : [cx, cy];
              const w = S * 0.9, h = S * 0.75;
              return (
                <g key={`j${t}`}>
                  <rect x={jx - w / 2} y={jy - h / 2} width={w} height={h} rx={2} fill="none" stroke={TEAM[t]} strokeWidth={2.5} />
                  {[-1, 0, 1].map(k => <line key={k} x1={jx + k * w / 4} y1={jy - h / 2} x2={jx + k * w / 4} y2={jy + h / 2} stroke={TEAM[t]} strokeWidth={2} />)}
                </g>
              );
            })}
            {fx && (
              <g>
                <circle cx={cx} cy={cy} r={10} fill={EFFECT_TIMING[fx] === 'active' ? '#f9fafb' : '#1f2937'}
                  stroke={EFFECT_TIMING[fx] === 'static' ? '#f9fafb' : '#111827'} strokeWidth={1.5} />
                <text x={cx} y={cy + 0.5} fontSize={10} fontWeight={700} textAnchor="middle" dominantBaseline="central"
                  fill={EFFECT_TIMING[fx] === 'static' ? '#f9fafb' : '#111827'} fontFamily="ui-monospace, monospace">{GLYPH[fx]}</text>
                <title>{`Cell ${c.id}: ${EFFECT_NAMES[fx]}`}</title>
              </g>
            )}
          </g>
        );
      })}
      {/* The active unit's guard zone: where it can strike */}
      {activeId && zoneHexes(s, s.units.find(u => u.id === activeId)!).filter(h => geom.cellAt.has(key(h))).map(h => (
        <polygon key={`r${key(h)}`} points={hexPoints(h, 0.96)} fill="#f87171" opacity={0.18} pointerEvents="none" />
      ))}
      {/* A unit being looked at: its whole guard zone, tinted in its team's colour */}
      {focus && focus !== activeId && (() => {
        const f = s.units.find(u => u.id === focus);
        return f && f.pos ? zoneHexes(s, f).filter(h => geom.cellAt.has(key(h))).map(h => (
          <polygon key={`fz${key(h)}`} points={hexPoints(h, 0.96)} fill={TEAM[f.team]} opacity={0.22} pointerEvents="none" />
        )) : null;
      })()}
      {/* Guard zones that stop movement (units that haven't activated this round, on guard, or sentinels): dashed outline */}
      {(['A', 'B'] as Team[]).map(t => {
        const seen = new Set<string>();
        return s.units.filter(u => u.team === t && zoneUp(s, u) && !u.inPrison && u.pos && u.id !== activeId).flatMap(u =>
          zoneHexes(s, u).filter(h => {
            const k = key(h);
            if (seen.has(k) || !geom.cellAt.has(k) || unitAtKey.has(k)) return false;
            seen.add(k);
            return true;
          }).map(h => (
            <polygon key={`z${t}${key(h)}`} points={hexPoints(h, t === 'A' ? 0.78 : 0.62)} fill="none" stroke={TEAM[t]}
              strokeWidth={2} strokeDasharray="3 2.5" opacity={0.9} pointerEvents="none" />
          )));
      })}
      {/* Guard zones that only tag (open zone): small diamonds, never the dashed outline; like other zones, gone once the unit has activated (unless on guard) */}
      {s.units.filter(u => hasTrait(s, u, 'noGuardZone') && (!u.activated || u.guarding) && !u.inPrison && u.pos && u.id !== activeId).flatMap(u =>
        zoneHexes(s, u).filter(h => geom.cellAt.has(key(h)) && !unitAtKey.has(key(h))).map(h => {
          const [x, y] = px(h);
          const d = u.team === 'A' ? 5 : 4;
          return <polygon key={`t${u.id}${key(h)}`} points={`${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`} fill="none" stroke={TEAM[u.team]}
            strokeWidth={1.6} opacity={0.9} pointerEvents="none" />;
        }))}
      {/* Flags on the ground */}
      {(['A', 'B'] as Team[]).map(t => {
        const f = s.flags[t];
        if (!f.pos) return null;
        const [x, y] = px(f.pos);
        return <Flag key={`f${t}`} x={x + 2} y={y - 1} team={t} />;
      })}
      {/* Move and target hints */}
      {Array.from(hints.entries()).map(([k, hint]) => {
        const [q, r] = k.split(',').map(Number);
        const [x, y] = px({ q, r });
        if (hint.kind === 'move') return <circle key={`h${k}`} cx={x} cy={y} r={5} fill="#fbbf24" opacity={0.9} onClick={() => onHexClick({ q, r })} style={{ cursor: 'pointer' }} />;
        if (hint.kind === 'select') return <polygon key={`h${k}`} points={hexPoints({ q, r }, 0.9)} fill="none" stroke="#fbbf24" strokeWidth={3} pointerEvents="none" />;
        if (hint.kind === 'drop') return <polygon key={`h${k}`} points={hexPoints({ q, r }, 0.9)} fill="#fbbf24" opacity={0.35} stroke="#fbbf24" strokeWidth={2} pointerEvents="none" />;
        if (hint.kind === 'area') return <polygon key={`h${k}`} points={hexPoints({ q, r }, 0.96)} fill="#fbbf24" opacity={0.28} pointerEvents="none" />;
        if (hint.kind === 'peek') return <circle key={`h${k}`} cx={x} cy={y} r={3.6} fill="none" stroke="#e5e7eb" strokeWidth={1.4} opacity={0.75} pointerEvents="none" />;
        if (hint.kind === 'reach') return <circle key={`h${k}`} cx={x} cy={y} r={2.8} fill="#fbbf24" opacity={0.6} onClick={() => onHexClick({ q, r })} style={{ cursor: 'pointer' }} />;
        return null;
      })}
      {/* Units, and prisoners standing in their prison (faded, behind bars) */}
      {s.units.filter(u => u.pos).map(u => {
        const [x, y] = px(u.pos!);
        const hint = hints.get(key(u.pos!));
        const done = u.inPrison || (u.activated && !u.guarding && u.id !== activeId);
        const [dx, dy] = px(DIRECTIONS[u.facing]);
        const len = Math.hypot(dx, dy), ux = dx / len, uy = dy / len;
        const tip = [x + ux * S * 0.98, y + uy * S * 0.98], bx = x + ux * S * 0.55, by = y + uy * S * 0.55;
        const wedge = `${tip[0]},${tip[1]} ${bx - uy * 6},${by + ux * 6} ${bx + uy * 6},${by - ux * 6}`;
        return (
          <g key={u.id} onClick={() => onHexClick(u.pos!)} style={{ cursor: clickAll || hint ? 'pointer' : 'default' }} opacity={done ? 0.5 : 1}>
            <title>{`${u.id} (${u.role})${u.inPrison ? ' · in prison' : ''}${u.carrying ? ' · carrying the flag' : ''}${u.guarding ? ' · on guard' : u.activated ? ' · activated this round' : ''}`}</title>
            {u.id === activeId && <circle cx={x} cy={y} r={S * 0.82} fill="none" stroke="#fbbf24" strokeWidth={3.5} />}
            {hint?.kind === 'activate' && <circle cx={x} cy={y} r={S * 0.82} fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="3 3" />}
            <circle cx={x} cy={y} r={S * 0.6} fill={TEAM[u.team]} stroke={hasTrait(s, u, 'heavy') ? '#f9fafb' : 'none'} strokeWidth={hasTrait(s, u, 'heavy') ? 2 : 0} />
            <polygon points={wedge} fill={TEAM[u.team]} stroke="#111827" strokeWidth={1} />
            <text x={x} y={y + 0.5} fontSize={13} fontWeight={700} fill="#fff" textAnchor="middle" dominantBaseline="central" fontFamily="system-ui, sans-serif">{unitLabel(s, u)}</text>
            {u.inPrison && [-1, 0, 1].map(k => <line key={k} x1={x + k * 5} y1={y - S * 0.62} x2={x + k * 5} y2={y + S * 0.62} stroke="#e5e7eb" strokeWidth={1.8} strokeLinecap="round" pointerEvents="none" />)}
            {u.carrying === 'flag' && <Flag x={x + 11} y={y - 12} team={u.team === 'A' ? 'B' : 'A'} scale={0.8} />}
            {u.guarding && <path d={`M${x - 16} ${y - 17} h10 v5 q0 6 -5 9 q-5 -3 -5 -9 z`} fill="#f9fafb" stroke="#111827" strokeWidth={1.2} pointerEvents="none" />}
            {hint?.kind === 'tag' && (
              <g>
                <circle cx={x} cy={y} r={S * 0.85} fill="none" stroke="#f87171" strokeWidth={3} />
                {hint.label && (
                  <text x={x} y={y - S * 1.15} fontSize={11} fontWeight={700} fill="#fecaca" textAnchor="middle" dominantBaseline="central"
                    stroke="#111827" strokeWidth={3} paintOrder="stroke" fontFamily="ui-monospace, monospace">{hint.label}</text>
                )}
              </g>
            )}
          </g>
        );
      })}
      {/* Last tag attempt */}
      {lastTag && (() => {
        const [x1, y1] = px(lastTag.from), [x2, y2] = px(lastTag.to);
        return (
          <g pointerEvents="none">
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={lastTag.hit ? '#f87171' : '#9ca3af'} strokeWidth={2.5} strokeDasharray={lastTag.hit ? undefined : '3 4'} />
            <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 12} fontSize={11} fontWeight={700} textAnchor="middle" fill={lastTag.hit ? '#fca5a5' : '#d1d5db'}
              stroke="#111827" strokeWidth={3} paintOrder="stroke" fontFamily="ui-monospace, monospace">{lastTag.hit ? 'TAG' : 'miss'}</text>
          </g>
        );
      })()}
    </svg>
  );
}

function Flag({ x, y, team, scale = 1 }: { x: number; y: number; team: Team; scale?: number }) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`} pointerEvents="none">
      <line x1={-5} y1={-10} x2={-5} y2={9} stroke="#f9fafb" strokeWidth={1.5} />
      <path d="M-4 -10 L9 -5 L-4 0 Z" fill={TEAM[team]} stroke="#f9fafb" strokeWidth={0.8} />
    </g>
  );
}


